/**
 * Resources sagas
 */
import { call, put, takeLatest } from 'redux-saga/effects';
import { toast } from 'react-toastify';

import { httpRequest } from '../../services/http-service';
import { endPoints } from '../../services/endpoints';
import {
  fetchResources,
  fetchResource,
  createResource,
  updateResourceAction,
  deleteResource,
  setResources,
  setCurrentResource,
  addResource,
  updateResource,
  removeResource,
  setLoading,
  setError,
} from './resourcesSlice';
import { presentLoader, dismissLoader } from '../../store/slices/commonSlice';

/**
 * Fetch all resources saga
 */
function* fetchResourcesSaga(action: ReturnType<typeof fetchResources>): Generator<any, void, any> {
  try {
    yield put(presentLoader());
    yield put(setLoading(true));

    const params: { activeOnly?: boolean; page?: number; limit?: number } = {
      activeOnly: false,
      page: 1,
      limit: 10,
    };

    if (action.payload) {
      if (action.payload.page !== undefined) params.page = action.payload.page;
      if (action.payload.limit !== undefined) params.limit = action.payload.limit;
      if (action.payload.activeOnly !== undefined) params.activeOnly = action.payload.activeOnly;
    }

    const response = yield call(httpRequest.get, endPoints.resources.getAll, {
      params,
    });

    if (response?.success) {
      yield put(setResources(response.data || response));
    } else {
      toast.error('Failed to fetch resources');
      yield put(setError('Failed to fetch resources'));
    }
  } catch (error: any) {
    const message = error?.message || 'Failed to fetch resources';
    toast.error(message);
    yield put(setError(message));
  } finally {
    yield put(setLoading(false));
    yield put(dismissLoader());
  }
}

/**
 * Fetch single resource saga
 */
function* fetchResourceSaga(action: ReturnType<typeof fetchResource>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));

    const response = yield call(httpRequest.get, endPoints.resources.getById(action.payload));

    if (response?.success) {
      yield put(setCurrentResource(response.data || response));
    } else {
      toast.error('Failed to fetch resource');
      yield put(setError('Failed to fetch resource'));
    }
  } catch (error: any) {
    const message = error?.message || 'Failed to fetch resource';
    toast.error(message);
    yield put(setError(message));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Create resource saga
 */
function* createResourceSaga(action: ReturnType<typeof createResource>): Generator<any, void, any> {
  try {
    yield put(presentLoader());
    yield put(setLoading(true));

    const response = yield call(httpRequest.post, endPoints.resources.create, action.payload);

    if (response?.success) {
      const newResource = response.data || response;
      yield put(addResource(newResource));
    } else {
      toast.error('Failed to create resource');
      yield put(setError('Failed to create resource'));
    }
  } catch (error: any) {
    const message = error?.message || 'Failed to create resource';
    toast.error(message);
    yield put(setError(message));
  } finally {
    yield put(setLoading(false));
    yield put(dismissLoader());
  }
}

/**
 * Update resource saga
 */
function* updateResourceSaga(action: ReturnType<typeof updateResourceAction>): Generator<any, void, any> {
  try {
    yield put(presentLoader());
    yield put(setLoading(true));

    const response = yield call(
      httpRequest.put,
      endPoints.resources.update(action.payload.id),
      action.payload.data
    );

    if (response?.success) {
      const updatedResource = { ...response.data, _id: action.payload.id };
      yield put(updateResource(updatedResource));
    } else {
      toast.error('Failed to update resource');
      yield put(setError('Failed to update resource'));
    }
  } catch (error: any) {
    const message = error?.message || 'Failed to update resource';
    toast.error(message);
    yield put(setError(message));
  } finally {
    yield put(setLoading(false));
    yield put(dismissLoader());
  }
}

/**
 * Delete resource saga
 */
function* deleteResourceSaga(action: ReturnType<typeof deleteResource>): Generator<any, void, any> {
  try {
    yield put(presentLoader());
    yield put(setLoading(true));

    const response = yield call(httpRequest.delete, endPoints.resources.delete(action.payload));

    if (response?.success || response) {
      yield put(removeResource(action.payload));
    } else {
      toast.error('Failed to delete resource');
      yield put(setError('Failed to delete resource'));
    }
  } catch (error: any) {
    const message = error?.message || 'Failed to delete resource';
    toast.error(message);
    yield put(setError(message));
  } finally {
    yield put(setLoading(false));
    yield put(dismissLoader());
  }
}

export function* resourcesSagas(): Generator<any, void, any> {
  yield takeLatest(fetchResources, fetchResourcesSaga);
  yield takeLatest(fetchResource, fetchResourceSaga);
  yield takeLatest(createResource, createResourceSaga);
  yield takeLatest(updateResourceAction, updateResourceSaga);
  yield takeLatest(deleteResource, deleteResourceSaga);
}

